import React, { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { MagnifyingGlass, PingPong } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  Tabs,
  TabsList,
  TabsTrigger
} from '@shopflo/ui'
import { permissionType } from 'src/lib/utils/constants'
import { useSegmentTemplates } from 'src/lib/hooks/useSegmentTemplates'
import useGTM from 'src/lib/hooks/useGTM'
import { SegmentTemplateType } from 'src/lib/types/segment'
import { useUser } from 'src/lib/contexts/UserContext'
import { Tooltip } from '@mui/material'
import { useLocale } from 'src/lib/hooks/useLocal'

interface TemplateDropdownProps {
  trigger: React.ReactNode
  onTemplateSelect: (template: SegmentTemplateType) => void
}

const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
  trigger,
  onTemplateSelect
}) => {
  const { t } = useLocale()
  const { sendGTMTrigger } = useGTM()
  const { canAccess } = useUser()
  const hasDynamicSegmentsPermission = canAccess(permissionType.DYNAMIC_SEGMENTS, 0)

  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [isOpen, setIsOpen] = useState(false)

  const { templates, categories, isLoading, error } = useSegmentTemplates()

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch =
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesFilter = activeFilter === 'all' || template.category === activeFilter
      return matchesSearch && matchesFilter
    })
  }, [templates, searchTerm, activeFilter])

  const handleTemplateClick = (template: SegmentTemplateType) => {
    sendGTMTrigger('segment_template_selected', 'click', { templateId: template.id })
    onTemplateSelect(template)
    setIsOpen(false)
    setSearchTerm('')
    setActiveFilter('all')
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSearchTerm('')
      setActiveFilter('all')
    }
  }

  if (!hasDynamicSegmentsPermission) {
    return (
      <Tooltip title={t('segment_creation.upgrade_plan_message')}>
        <span className='inline-block opacity-50 cursor-not-allowed'>{trigger}</span>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='w-[532px] max-w-[min(532px,calc(100vw-2.5rem))] p-0 bg-white rounded-xl overflow-hidden min-w-0'
        side='bottom'
        align='end'
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={20}
        sticky='always'
      >
        {/* Search Header */}
        <div className='px-1.5 py-0.5 bg-zinc-50 border-b border-zinc-950/8'>
          <Input
            type='text'
            variant='filled'
            dimension='md'
            placeholder={t('segment_creation.or_search_template')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            startAdornment={<MagnifyingGlass size={18} className='text-zinc-400' />}
            className='!bg-transparent !shadow-none focus-within:!shadow-none focus-within:!bg-transparent'
            autoFocus
          />
        </div>

        {/* Category Tabs */}
        <div className='px-2 py-2'>
          <Tabs value={activeFilter} onValueChange={setActiveFilter}>
            <TabsList variant='neutral' size='sm'>
              <TabsTrigger value='all'>{t('segment_creation.all_filter')}</TabsTrigger>
              {categories.map(category => (
                <TabsTrigger key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ')}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Divider */}
        <div className='h-[12px] border-t-2 border-dotted border-zinc-950/8' />

        {/* Template List — skeleton ↔ content crossfade (matches SegmentDefinitionPanel) */}
        <div className='flex flex-col min-h-[200px] max-h-[400px] min-w-0 overflow-y-auto overflow-x-hidden px-1.5'>
          <AnimatePresence mode='popLayout'>
            {isLoading ? (
              <motion.div
                key='template-dropdown-skeleton'
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
                transition={{ duration: 0 }}
                className='flex min-w-0 flex-col gap-0 px-1.5 py-1'
                aria-busy='true'
              >
                {(
                  [
                    ['w-[88%]', 'opacity-100'],
                    ['w-[58%]', 'opacity-70'],
                    ['w-[96%]', 'opacity-40'],
                    ['w-[70%]', 'opacity-10']
                  ] as const
                ).map(([widthClass, rowOpacityClass], i) => (
                  <div
                    key={i}
                    className={`flex min-w-0 max-w-full w-full flex-row items-center gap-2 px-2 py-1.5 ${rowOpacityClass}`}
                  >
                    <div className='h-5 w-5 shrink-0 animate-pulse rounded-sm bg-zinc-200/90' />
                    <div className='min-w-0 flex-1'>
                      <div className={`h-4 max-w-full animate-pulse rounded-md bg-zinc-200/90 ${widthClass}`} />
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key='template-dropdown-content'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
                exit={{ opacity: 1, transition: { duration: 0 } }}
                className='flex min-w-0 flex-col'
              >
                {error ? (
                  <div className='flex items-center justify-center p-8'>
                    <span className='text-text-sm-regular text-zinc-500'>
                      {t('segment_creation.error_loading_templates')}
                    </span>
                  </div>
                ) : filteredTemplates.length > 0 ? (
                  filteredTemplates.map((template) => (
                    <Tooltip
                      key={template.id}
                      title={!hasDynamicSegmentsPermission ? t('segment_creation.upgrade_plan_message') : ''}
                    >
                      <span className='block w-full min-w-0 max-w-full'>
                        <button
                          type='button'
                          onClick={() => hasDynamicSegmentsPermission && handleTemplateClick(template)}
                          disabled={!hasDynamicSegmentsPermission}
                          className='flex min-w-0 max-w-full w-full flex-row items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-zinc-50 text-left disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                          <Icon IconComponent={PingPong} size='sm' className='shrink-0 text-zinc-400' />
                          <span className='min-w-0 flex-1 truncate text-left text-text-sm-regular text-zinc-600'>
                            {template.name}
                          </span>
                        </button>
                      </span>
                    </Tooltip>
                  ))
                ) : (
                  <div className='flex items-center justify-center p-8'>
                    <span className='text-text-sm-regular text-zinc-500'>{t('segment_creation.no_templates_found')}</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TemplateDropdown
